import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

export type DrawerPosition = 'right' | 'bottom';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  position?: DrawerPosition;
  width?: string;
  showCloseButton?: boolean;
  className?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  position = 'right',
  width = 'max-w-md',
  showCloseButton = true,
  className = '',
}) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const isRight = position === 'right';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 overflow-hidden flex justify-end"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={`
          relative z-10 w-full bg-[#0F172A]/95 border-white/10
          shadow-[-10px_0_40px_rgba(0,0,0,0.8)] backdrop-blur-2xl
          flex flex-col overflow-hidden
          ${
            isRight
              ? `h-full ${width} border-l animate-slideInRight`
              : 'max-h-[85vh] rounded-t-3xl border-t bottom-0 animate-slideInUp'
          }
          ${className}
        `}
      >
        {/* Mobile drag handle for bottom sheet */}
        {!isRight && (
          <div className="flex justify-center pt-2.5 pb-1">
            <div className="w-12 h-1.5 rounded-full bg-slate-700" />
          </div>
        )}

        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
            <div>
              {title && (
                <h3 className="text-base sm:text-lg font-bold text-text-primary flex items-center gap-2">
                  {title}
                </h3>
              )}
              {subtitle && <p className="text-xs text-text-secondary mt-0.5">{subtitle}</p>}
            </div>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="
                  min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl
                  text-text-secondary hover:text-text-primary hover:bg-surface-hover
                  transition-colors focus:outline-none focus:ring-2 focus:ring-accent-emerald/50
                "
                aria-label="Закрити"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 custom-scrollbar text-text-primary text-sm">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-5 py-3.5 border-t border-white/10 bg-slate-900/60 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
